import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Goal: create a form to request data from the backend
const Container = styled.div`
  max-width: 600px;
  margin: 40px auto;
  padding: 20px;
  text-align: center;
`;

const DateContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 20px 0;
`;

const DateInput = styled.input`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-left: 10px;
`;
/*
const UsagePointInput = styled.input`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin: 10px;
  width: 200px;
`;
*/

const Button = styled.button`
  padding: 12px 20px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin: 10px;

  &:hover {
    background-color: #45a049;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin: 20px 0;
  padding: 10px;
  border-radius: 4px;
  background-color: ${props => {
    if (props.$type === 'error') return '#fee';
    if (props.$type === 'warning') return '#fff3cd';
    return '#efe';
  }};
  color: ${props => {
    if (props.$type === 'error') return '#c00';
    if (props.$type === 'warning') return '#856404';
    return '#0c0';
  }};
  border: 1px solid ${props => {
    if (props.$type === 'error') return '#f5c6cb';
    if (props.$type === 'warning') return '#ffeaa7';
    return '#c3e6cb';
  }};
`;

function DataRequest() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [showDateForm, setShowDateForm] = useState(false);
  const [showExistingDataOptions, setShowExistingDataOptions] = useState(false);
  const [existingDataInfo, setExistingDataInfo] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Actions that require date range selection
  const actionsNeedingDates = ['all-usage-interval', 'all-auth-usage-interval', 'usage-summary'];

  // Validate that date range is at least 1 month
  const validateDateRange = (start, end) => {
    if (!start || !end) {
      return { isValid: false, message: 'Both start and end dates are required.' };
    }

    const startDate = new Date(start);
    const endDate = new Date(end);
    
    if (startDate >= endDate) {
      return { isValid: false, message: 'Start date must be before end date.' };
    }

    // Check if range is at least 1 month (30 days)
    const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
    if (daysDiff < 30) {
      return { 
        isValid: false, 
        message: 'Date range must be at least 30 days. Please select a longer period.' 
      };
    }

    // Inform user about chunking for large ranges
    if (daysDiff > 60) {
      const chunks = Math.ceil(daysDiff / 60);
      return { 
        isValid: true, 
        message: `Note: Date range will be processed in ${chunks} chunks of 60 days each to comply with API limits.`,
        isWarning: true
      };
    }

    return { isValid: true, message: '' };
  };

  const handleActionClick = (endpoint) => {
    setMessage(null);
    setSelectedAction(endpoint);
    
    if (actionsNeedingDates.includes(endpoint)) {
      // Set default date range (last 3 months)
      const today = new Date();
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      
      setEndDate(today.toISOString().split('T')[0]);
      setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
      setShowDateForm(true);
    } else {
      // Proceed directly for actions that don't need dates
      handleRequest(endpoint);
    }
  };

  const handleDateSubmit = () => {
    const validation = validateDateRange(startDate, endDate);
    
    if (!validation.isValid) {
      setMessage({
        type: 'error',
        text: validation.message
      });
      return;
    }

    // Show warning message if date range will be chunked
    if (validation.isWarning) {
      setMessage({
        type: 'warning',
        text: validation.message
      });
    }

    setShowDateForm(false);
    handleRequest(selectedAction);
  };

  const handleDateCancel = () => {
    setShowDateForm(false);
    setSelectedAction(null);
    setMessage(null);
  };

  const handleViewExistingData = () => {
    if (existingDataInfo.type === 'COMPLETE_ENERGY_DATA') {
      // For now, show a message that energy data viewing is not yet implemented
      // In the future, this would navigate to an energy data panel
      setMessage({
        type: 'info',
        text: `Energy data viewing panel is under development. Your ${existingDataInfo.existingData.records} energy records (${existingDataInfo.existingData.coveragePercentage}% coverage) are safely stored in the database.`
      });
      setShowExistingDataOptions(false);
    } else {
      // Customer data - navigate to user info panel
      navigate('/user-info');
    }
  };

  const handleReRequestData = () => {
    setShowExistingDataOptions(false);
    setExistingDataInfo(null);
    // Re-call with forceRefresh parameter
    handleRequest(selectedAction, true);
  };

  const handleExistingDataCancel = () => {
    setShowExistingDataOptions(false);
    setSelectedAction(null);
    setExistingDataInfo(null);
    setMessage(null);
  };

  const handleRequest = async (endpoint, forceRefresh = false) => {
    setLoading(true);
    setMessage(null);
    
    try {
      // Ensure we're using the correct base URL
      const baseUrl = process.env.REACT_APP_API_URL;
      let url = `${baseUrl}/data`;
      
      const endpointMap = {
        'single-auth-customer': '/customer',
        'all-auth-customer': '/bulk/customer',
        'all-usage-interval': '/batch/subscription',
        'all-auth-usage-interval': '/batch/bulk',
        'usage-summary': '/usage-summary'
      };

      url += endpointMap[endpoint];
      
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add date parameters for endpoints that need them
      if (actionsNeedingDates.includes(endpoint)) {
        params.append('startDate', startDate + 'T00:00:00Z');
        params.append('endDate', endDate + 'T23:59:59Z');
      }
      
      // Add forceRefresh parameter if needed
      if (forceRefresh) {
        params.append('forceRefresh', 'true');
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log('Making request to:', url); // Add this for debugging

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();
      
      // Handle existing data cases
      if (data.fromCache && data.options) {
        if (data.coverageType === 'COMPLETE') {
          // Complete energy data coverage
          setMessage({
            type: 'info', 
            text: `Complete energy data found in database (${data.existingData.coveragePercentage}% coverage, ${data.existingData.records} records covering ${data.existingData.coveredDays}/${data.existingData.requestedDays} days). View existing data or request fresh data?`
          });
          setExistingDataInfo({
            ...data,
            type: 'COMPLETE_ENERGY_DATA'
          });
        } else {
          // Customer info existing data
          setMessage({
            type: 'info',
            text: `Customer data found from ${new Date(data.lastUpdated).toLocaleDateString()}. Would you like to view existing data or request fresh data?`
          });
          setExistingDataInfo(data);
        }
        setShowExistingDataOptions(true);
        return;
      }
      
      // Handle successful request
      let successMessage = `Successfully requested ${endpoint} data.`;
      
      // Add specific information based on response type
      if (endpoint === 'usage-summary') {
        if (data.coverageType === 'PARTIAL' && data.coverage) {
          successMessage = `Partial data update completed! Added ${data.coverage.new.records} new records to existing ${data.coverage.existing.records} records (${data.coverage.existing.coveragePercentage}% previous coverage).`;
          if (data.summary) {
            successMessage += ` Processing details: ${data.summary.processed} processed: ${data.summary.successful}, ${data.summary.noData}, ${data.summary.errors}. ${data.summary.energyRecords}.`;
          }
        } else if (data.coverageType === 'FORCE_REFRESH') {
          successMessage = `Fresh energy data retrieved (force refresh requested).`;
          if (data.summary) {
            successMessage += ` ${data.summary.processed} processed: ${data.summary.successful}, ${data.summary.noData}, ${data.summary.errors}. ${data.summary.energyRecords}.`;
          }
        } else if (data.coverageType === 'NONE') {
          successMessage = `New energy data retrieved (no existing data found).`;
          if (data.summary) {
            successMessage += ` ${data.summary.processed} processed: ${data.summary.successful}, ${data.summary.noData}, ${data.summary.errors}. ${data.summary.energyRecords}.`;
          }
        } else if (data.summary) {
          // Default case
          successMessage += ` ${data.summary.processed} processed: ${data.summary.successful}, ${data.summary.noData}, ${data.summary.errors}. ${data.summary.energyRecords}.`;
        }
      } else if (data.requestId) {
        successMessage += ` Request ID: ${data.requestId}`;
      }
      
      setMessage({
        type: 'success',
        text: successMessage
      });
      setTimeout(() => navigate('/results'), 2000);
    } catch (error) {
      console.error('Data request error:', error);
      setMessage({
        type: 'error',
        text: error.message || 'An unexpected error occurred'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h1>Request Energy Data</h1>
      
      {showExistingDataOptions ? (
        <div>
          <h2>Existing Data Found</h2>
          <p>
            {existingDataInfo.type === 'COMPLETE_ENERGY_DATA' ? (
              <>
                Complete energy data found in database with {existingDataInfo.existingData.coveragePercentage}% coverage 
                ({existingDataInfo.existingData.records} records covering {existingDataInfo.existingData.coveredDays}/{existingDataInfo.existingData.requestedDays} days) 
                from {new Date(existingDataInfo.lastUpdated).toLocaleDateString()}.
                What would you like to do?
              </>
            ) : (
              <>
                Customer data was found in the database from {new Date(existingDataInfo.lastUpdated).toLocaleDateString()}.
                What would you like to do?
              </>
            )}
          </p>
          
          <div>
            <Button onClick={handleViewExistingData} disabled={loading}>
              View Existing Data
            </Button>
            <Button onClick={handleReRequestData} disabled={loading}>
              Re-Request Fresh Data
            </Button>
            <Button onClick={handleExistingDataCancel} disabled={loading} style={{backgroundColor: '#ccc', color: '#333'}}>
              Cancel
            </Button>
          </div>
        </div>
      ) : !showDateForm ? (
        <div>
          <p>Select the type of data you want to request:</p>
          <Button 
            onClick={() => handleActionClick('single-auth-customer')} 
            disabled={loading}
          >
            Single Authorization Customer Info
          </Button>
          {/* 
          <Button 
            onClick={() => handleActionClick('all-auth-customer')} 
            disabled={loading}
          >
            All Authorizations Customer Info
          </Button>
          */}
          {/* 
          <Button 
            onClick={() => handleActionClick('all-usage-interval')} 
            disabled={loading}
          >
            All Usage Points Interval
          </Button>
          <Button 
            onClick={() => handleActionClick('all-auth-usage-interval')} 
            disabled={loading}
          >
            All Authorization Usage Interval
          </Button>
          */}
          <Button 
            onClick={() => handleActionClick('usage-summary')} 
            disabled={loading}
          >
            Usage Summary
          </Button> 
        </div>
      ) : (
        <div>
          <h2>Select Date Range</h2>
          <p>Please select a date range (minimum 30 days) for your request:</p>
          <p style={{ fontSize: '0.9em', color: '#666' }}>
            Note: Large date ranges will be automatically split into 60-day chunks to comply with API limits.
          </p>
          
          <DateContainer>
            <div>
              <label>Start Date: </label>
              <DateInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
              />
            </div>
            <div>
              <label>End Date: </label>
              <DateInput
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
              />
            </div>
          </DateContainer>
          
          <div>
            <Button onClick={handleDateSubmit} disabled={loading}>
              Submit Request
            </Button>
            <Button onClick={handleDateCancel} disabled={loading} style={{backgroundColor: '#ccc', color: '#333'}}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {message && (
        <Message $type={message.type}>
          {message.text}
        </Message>
      )}

      {loading && <div>Loading...</div>}
    </Container>
  );
}

export default DataRequest;